/** The controls an uploaded MCE adapter can send: its table's rows that are not ignored. */
import { ControlMessageT } from "../../flatbuffers/generated/control/control-message";
import { MessageT } from "../../flatbuffers/generated/ln2/message";
import { MessageContent } from "../../flatbuffers/generated/ln2/message-content";
import { ArgSource } from "../../flatbuffers/generated/mce/arg-source";
import { CommandAddress } from "../../flatbuffers/generated/mce/command-address";
import { CommandRuleT } from "../../flatbuffers/generated/mce/command-rule";
import { IgnoreRuleT } from "../../flatbuffers/generated/mce/ignore-rule";
import { TranslationTableT } from "../../flatbuffers/generated/mce/translation-table";
import { readTranslationTable } from "./client";
import { unpackMapping } from "./messages";

/** One control a table's row can send, with how it expands. */
export interface MceControlEntry {
  enumValue: number;
  floorsBased: boolean;
  unitsBased: boolean;
}

/** The Control member a message names, or null when it is not a control. */
function controlOfMessage(message: MessageT | null): number | null {
  const encoded = message?.content;
  if (!encoded || encoded.messageType !== MessageContent.Control_ControlMessage) return null;
  const leaf = (encoded.message as ControlMessageT | null)?.control?.control;
  return leaf ? leaf.message : null;
}

/** The distinct controls of a table. A control sent by several rows expands as any of them does. */
export function controlEntriesOfTable(table: TranslationTableT): MceControlEntry[] {
  const entries = new Map<number, MceControlEntry>();
  table.controls.forEach((row) => {
    if (row.rule instanceof IgnoreRuleT) return;
    const enumValue = controlOfMessage(table.templates[row.match]?.msg?.msg ?? null);
    if (enumValue === null) return;
    const commands = row.rule instanceof CommandRuleT ? row.rule.commands : [];
    const known = entries.get(enumValue);
    entries.set(enumValue, {
      enumValue,
      floorsBased:
        (known?.floorsBased ?? false) ||
        commands.some((command) => command.args.some((arg) => arg.source === ArgSource.Position)),
      unitsBased:
        (known?.unitsBased ?? false) ||
        commands.some((command) => command.address === CommandAddress.Car),
    });
  });
  return [...entries.values()];
}

/** The controls of a stored Mapping. Empty for a legacy mapping, which has no table. */
export async function controlEntriesOfMapping(bytes: Uint8Array): Promise<MceControlEntry[]> {
  const table = await readTranslationTable(unpackMapping(bytes));
  return table ? controlEntriesOfTable(table) : [];
}
